<template lang="html">
<v-layout row align-end class="search-panel" wrap>
    <v-flex style="max-width:192px;" align-self-center>
        <h2>Students 3D</h2>
    </v-flex>
    <v-flex style="max-width:200px;">
        <fe-remote-combo
            class="pr-3"
            data-test="students3d-school-year-filter-btn"
            label="School Year"
            hide-details
            v-model="schoolYear"
            rootProperty="years"
            ref="schoolYearCombo"
            url="qry.php?action=get&property=school_years"
            :rules="[v => !!v || 'Field is required']"
        ></fe-remote-combo>
    </v-flex>

    <v-flex class="combo">
        <fe-remote-combo
            class="pr-3"
            data-test="students3d-schools-filter-btn"
            label="Schools"
            multiple moreText hide-details
            v-model="schools"
            rootProperty="schools"
            url="schools.php?action=get&incl_groups=1"
            :rules="[v => !!v || 'Field is required']"
            :disabled="!schoolYear"
            :params="{ school_year_id: schoolYear ? schoolYear.id : null }"
        ></fe-remote-combo>
    </v-flex>

    <v-flex class="combo">
        <fe-remote-combo
            class="pr-3"
            data-test="students3d-grades-filter-btn"
            label="Grades"
            multiple moreText hide-details
            v-model="grades"
            rootProperty="grades"
            url="grades.php?"
            :rules="[v => !!v || 'Field is required']"
            :disabled="!schoolYear"
            :params="{
                school_year_id: schoolYear ? schoolYear.id : null,
                school_id: schools ? schools.map(s => s.id) : null,
            }"
        ></fe-remote-combo>
    </v-flex>

    <v-flex >
        <FeSplitMenuBtn
            :btnAttrs="{ disabled: !schoolYear && !(sessionUser && sessionUser.currentYear) }"
            :close-on-content-click="false"
            offset-y
            style="position:relative; top:4px;"
            @click="handleSearchClick"
        >
            Search
            <template v-slot:menu>
                <v-list data-test="students3d-search-dropdown-menu" style="padding: 20px 5px;">
                    <v-list-item
                        style="width: 200px; height: 75px; margin-top: 20px;"
                    >
                        <v-list-item-content style="height: 88px;">
                            <div class="theme--light v-label">
                                Active Status
                            </div>
                            <v-select
                                :items="activeStatusItems"
                                v-model="student_active_flag"
                                flat solo dense
                            ></v-select>
                        </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                        <v-list-item-content style="height: 88px;">
                            <FeDatePicker
                                v-model="student_active_date"
                                label="Active as Of"
                                dateFormat="M/D/YYYY"
                                :disabled="isActiveDateLoading"
                            />
                            <div v-if="isActiveDateLoading" class="caption text--secondary mt-1">
                                <v-progress-circular
                                    indeterminate
                                    size="12"
                                    width="1"
                                    class="mr-1"
                                ></v-progress-circular>
                                Updating date...
                            </div>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </template>
        </FeSplitMenuBtn>

    </v-flex>


</v-layout>
</template>

<script>
import { mapState } from 'vuex'
import Students3D from '@/components/modules/students3d/View'

export default {
    name: 'DemographicSearch',
    components: {
        Students3D,
    },

    computed: {
        ...mapState('global',['sessionUser', 'defaultSchool']),
        params() {
            // Use either user selected value, or default value if no selection is made
            const effectiveYearId = this.schoolYear?.id ?? this.sessionUser?.currentYear?.id
            return {
                school_year_id: effectiveYearId,
                ...(this.schools?.length) && { school_id: this.schools.map(x => x.id) },
                ...(this.grades?.length) && { grade_id: this.grades.map(x => x.id) },
                ...(this.student_active_flag !== null) && { student_active_flag: this.student_active_flag },
                ...(this.student_active_date && this.student_active_date !== 'Invalid Date') && { student_active_date: this.student_active_date },
            }
        },
        currentYear() {
            return this.sessionUser.currentYear
        }
    },
    
    mounted() {
        this.resetSearch()
    },

    watch: {
        // Watch for sessionUser to become available
        sessionUser: {
            handler(newVal) {
                if (newVal && newVal.currentYear && !this.schoolYear) {
                    this.schoolYear = newVal.currentYear
                    this.$nextTick(() => {
                        this.updateActiveDate()
                    })
                }
            },
            deep: true,
            immediate: true
        },
        // Watch for changes to school year and trigger date update
        schoolYear: {
            handler(newVal, oldVal) {
                if (newVal && newVal !== oldVal) {
                    this.updateActiveDate()
                }
            },
            deep: true
        },
        // Watch for changes to schools and trigger date update
        schools: {
            handler(newVal, oldVal) {
                if (this.schoolYear && newVal !== oldVal) {
                    this.updateActiveDate()
                }
            },
            deep: true
        },
        // Watch for changes to grades and trigger date update
        grades: {
            handler(newVal, oldVal) {
                if (this.schoolYear && newVal !== oldVal) {
                    this.updateActiveDate()
                }
            },
            deep: true
        }
    },
    
    data() {
        return {
            feDialog: false,
            cmp: Students3D,

            schoolYear: null,
            schools: null,
            grades: null,
            student_active_flag: 1,
            student_active_date: null,
            activeStatusItems: [
                { text: 'Inactive', value: 0 },
                { text: 'Active', value: 1 },
                { text: 'Both', value: null },
            ],

            // Loading state tracking
            isActiveDateLoading: false,
            pendingSearchRequest: false,
            skipActiveDateUpdate: false,
        }
    },

    methods: {
        resetSearch() {
            this.schoolYear = this.sessionUser.currentYear
            this.schools = this.defaultSchool ? [this.defaultSchool] : null
            this.grades = null
            this.student_active_flag = 1
            this.student_active_date = this.$dayjs().format('YYYY-MM-DD')
            // Set initial active date based on current filters
            this.$nextTick(() => {
                this.updateActiveDate()
            })
        },
        getCurrentSearchParams() {
            return this.params
        },
        handleSearchClick() {
            this.$emit('search-clicked')
            // If no year selected, use same default as dropdown so Search works; sync dropdown to show it
            if (!this.schoolYear?.id && this.sessionUser?.currentYear) {
                this.schoolYear = this.sessionUser.currentYear
            }
            this.$emit('coordinated-search', this.params)
            
            if (this.isActiveDateLoading) {
                // If active date is still loading, wait for it to complete before executing
                this.pendingSearchRequest = true
            } else {
                // Active date is ready, execute search immediately
                this.executeActualSearch()
            }
        },
        
        executeActualSearch() {
            // Clear any pending search flag
            this.pendingSearchRequest = false
            this.$emit('input', this.params)
        },

        async updateActiveDate() {
            // Skip if agent is setting values programmatically
            if (this.skipActiveDateUpdate) {
                return
            }

            // Only proceed if we have a school year
            if (!this.schoolYear || !this.schoolYear.id) {
                return
            }

            // Set loading state
            this.isActiveDateLoading = true

            try {
                // Build query parameters
                const params = {
                    school_year_id: this.schoolYear.id
                }

                // Add school IDs if selected
                if (this.schools && this.schools.length > 0) {
                    params.school_ids = this.schools.map(s => s.id).join(',')
                }

                // Add grade IDs if selected
                if (this.grades && this.grades.length > 0) {
                    params.grade_ids = this.grades.map(g => g.id).join(',')
                }

                const response = await this.$axios.get(this.$models.getUrl('schoolCalendarEarliestEndDate', 'read'), { params })
                
                // Update the active date - API now always returns a valid date
                if (response.data && response.data.earliest_end_date) {
                    this.student_active_date = response.data.earliest_end_date
                }
            } catch (error) {
                console.error('Error fetching earliest end date:', error)
                // Don't change the current date if there's an error
            } finally {
                // Clear loading state
                this.isActiveDateLoading = false
                
                // If there's a pending search request, execute it now with the updated date
                if (this.pendingSearchRequest) {
                    this.$nextTick(() => {
                        this.executeActualSearch()
                    })
                }
            }
        }
    }
}
</script>

<style lang="css" scoped>
h2 {
    padding-top: 8px;
    font-size: 24px;
    font-weight: 600;
}
.combo {
    width: 200px;
    min-width: 200px;
    max-width: 360px;
}
.search-panel {
    padding: 16px;
    background-color: white;
    border-bottom: 1px solid #E0E0E0;
    flex: 0 0 auto !important;
    height: auto !important;
    min-height: 0;
}
</style>
